import type { DragEvent, MouseEvent, ReactNode, CSSProperties } from "react";
import { Card } from "./Card";
import "./layout.css";

export interface KanbanCardProps {
  title: string;
  subtitle?: string;
  value?: string;
  tags?: ReactNode;
  href?: string;
  onClick?: () => void;
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: (e: DragEvent) => void;
  /** Soften the card after convert/win while it remains on the board. */
  muted?: boolean;
  /** Runtime background from org board-display settings (never a source hex literal). */
  backgroundColor?: string;
}

/**
 * Org-picked aging colors are stored as fixed hex values chosen for light-mode
 * legibility (dark text on a pale swatch). The theme's default text color
 * flips to light in dark mode, so a literal background needs its own
 * luminance-derived text color rather than inheriting the page's token.
 */
function readableTextColor(hex: string): string | undefined {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return undefined;
  const value = match[1];
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  const channel = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.5 ? "var(--brand-900)" : "var(--neutral-0)";
}

function cardForeground(background: string): string | undefined {
  return background.trim().startsWith("var(")
    ? "var(--color-board-card-foreground, var(--color-text))"
    : readableTextColor(background);
}

/** A single card on a board column — deal/opportunity-shaped by default. */
export function KanbanCard({
  title,
  subtitle,
  value,
  tags,
  href,
  onClick,
  draggable,
  onDragStart,
  onDragEnd,
  muted,
  backgroundColor,
}: KanbanCardProps) {
  const clickable = Boolean(href || onClick);
  function handleLinkClick(event: MouseEvent<HTMLAnchorElement>) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    onClick?.();
  }
  const style: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
    minHeight: "var(--size-kanban-card-min-height, 112px)",
    cursor: clickable ? "pointer" : draggable ? "grab" : undefined,
  };
  const onColor = backgroundColor ? cardForeground(backgroundColor) : undefined;
  if (backgroundColor) {
    style.background = backgroundColor;
    if (onColor) {
      style.color = onColor;
      (style as CSSProperties & { "--kanban-card-fg"?: string })["--kanban-card-fg"] = onColor;
    }
  }
  if (muted) {
    style.opacity = backgroundColor ? 0.88 : 0.72;
    style.boxShadow = "none";
    style.border = "1px solid var(--color-border-strong)";
    if (!backgroundColor) {
      style.background = "var(--color-surface-muted)";
      style.color = "var(--color-text-muted)";
    }
  }
  return (
    <Card
      className={`ui-kanban-card${muted ? " ui-kanban-card--muted" : ""}${backgroundColor ? " ui-kanban-card--colored" : ""}`}
      interactive={clickable && !muted}
      padding="sm"
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={style}
    >
      {href ? (
        <a className="ui-kanban-card__link" href={href} draggable={false} onClick={handleLinkClick}>
          {title}
        </a>
      ) : (
        <div className="ui-kanban-card__title" style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-sm)", lineHeight: 1.35 }}>{title}</div>
      )}
      {subtitle && (
        <div
          className="ui-kanban-card__subtitle"
          style={{ fontSize: "var(--font-size-xs)", lineHeight: 1.35, color: muted ? "var(--color-text-muted)" : onColor ?? "var(--color-text-muted)" }}
        >
          {subtitle}
        </div>
      )}
      {(value || tags) && (
        <div className="ui-kanban-card__footer">
          {value && (
            <span
              style={{
                fontWeight: "var(--font-weight-bold)",
                fontSize: "var(--font-size-sm)",
                lineHeight: 1.35,
                color: muted ? "var(--color-text-muted)" : onColor ?? "var(--color-accent)",
              }}
            >
              {value}
            </span>
          )}
          {tags}
        </div>
      )}
    </Card>
  );
}
